import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Lock, Sparkles, ArrowRight, CheckCircle2, Clock, Layers, Pencil, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import BundleInterestDialog from "@/components/BundleInterestDialog";
import api, { API as API_BASE } from "@/lib/api";
import { trackViewItem } from "@/lib/analytics";

const DeckCard = ({ deck, index, levelName, price, levelId }) => (
  <Card className="overflow-hidden border-slate-200 hover:border-teal-300 transition-colors duration-300" data-testid={`ai-deck-${deck.id}`}>
    <a href={`${API_BASE}/resources/${deck.id}/preview`} target="_blank" rel="noreferrer" className="block bg-slate-100 border-b border-slate-200">
      {deck.has_thumbnail
        ? <img src={`${API_BASE}/resources/${deck.id}/thumbnail`} loading="lazy" alt={`${deck.title} — sample slide`} className="w-full aspect-[16/9] object-cover object-top" />
        : <div className="w-full aspect-[16/9]" />}
    </a>
    <div className="p-6">
      <span className="font-display text-3xl font-bold text-slate-200 leading-none">{String(index + 1).padStart(2, "0")}</span>
      <h3 className="font-display text-lg font-bold text-slate-900 mt-2">{deck.title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{deck.summary}</p>
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`ai-deck-preview-${deck.id}`}>
          <a href={`${API_BASE}/resources/${deck.id}/preview`} target="_blank" rel="noreferrer"
             onClick={() => trackViewItem({ item_id: deck.id, name: deck.title, price, kind: "ai_workshop" })}>
            <Eye className="w-4 h-4" /> Preview a slide
          </a>
        </Button>
        <span className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid={`ai-deck-included-${deck.id}`}>
          <Lock className="w-3 h-3" /> Included in {levelName} · ${price}
        </span>
      </div>
    </div>
  </Card>
);

function Level({ level, index }) {
  const open = level.status === "available";
  const levelLabel = `${level.level} — ${level.title}`;
  return (
    <Card className={`p-7 sm:p-9 ${open ? "border-teal-200 bg-white" : "border-slate-200 bg-slate-50/60"}`} data-testid={`ai-level-${level.slug}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-bold text-teal-700 tracking-widest uppercase">Level {index + 1}</span>
            {open
              ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Available now</Badge>
              : <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200"><Clock className="w-3 h-3 mr-1" /> Coming soon</Badge>}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2">{level.level} — {level.title}</h2>
          <p className="font-serif-editorial italic text-teal-700 mt-1">{level.tagline}</p>
          <p className="text-base text-slate-600 mt-4 max-w-2xl leading-relaxed">{level.description}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-4xl font-bold text-slate-900">${level.price}</p>
          <p className="text-xs text-slate-500 mt-1">all {level.workshop_count} workshops</p>
        </div>
      </div>

      {level.outcomes?.length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-6">
          {level.outcomes.map((o) => (
            <li key={o} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> {o}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-7">
        {open && level.available ? (
          <>
            <BuyButton itemId={level.id} itemName={levelLabel} price={level.price} kind="bundle"
                       className="bg-teal-600 hover:bg-teal-700 rounded-full h-12 px-7" testId={`ai-buy-${level.slug}`}>
              Get all {level.workshop_count} workshops · ${level.price}
            </BuyButton>
            <span className="text-sm text-slate-500">One payment. Every deck in this level, instantly.</span>
          </>
        ) : (
          <BundleInterestDialog bundle={{ id: level.id, title: levelLabel }}
                                triggerLabel={`Tell me when ${level.level} opens`}
                                triggerClassName="rounded-full h-12 px-7" />
        )}
      </div>

      {level.decks.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mt-9 pt-9 border-t border-slate-200" data-testid={`ai-level-decks-${level.slug}`}>
          {level.decks.map((d, i) => (
            <DeckCard key={d.id} deck={d} index={i} levelName={`${level.level} level`} price={level.price} levelId={level.id} />
          ))}
        </div>
      )}
    </Card>
  );
}

export default function AiWorkshops() {
  const [levels, setLevels] = useState([]);

  useEffect(() => { api.get("/ai-levels").then((r) => setLevels(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="ai-workshops-page">
      <PageHero
        eyebrow="AI professional learning"
        title="Use AI where it helps. Be human where it matters."
        subtitle="A three-level professional learning series for educators — from building a defensible position on AI, to working with it day to day, to designing systems that hold up across a school."
      >
        <div className="flex flex-wrap gap-3" data-testid="ai-hero-actions">
          <Button asChild className="rounded-full bg-teal-600 hover:bg-teal-700 h-12 px-7">
            <a href="#levels"><Layers className="w-4 h-4" /> See the three levels</a>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-7 bg-transparent text-white border-white/30 hover:bg-white hover:text-slate-900">
            <Link to="/resources">Resource store <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </PageHero>

      <Section className="!pb-0">
        <SectionHeading
          eyebrow="Why this series"
          title="Not an AI tools course. A professional judgement course."
          subtitle="Written for any educator — pre-service, graduate, classroom teacher or school leader. The goal is neither maximum AI use nor avoiding it altogether: it's worthwhile assistance, guided by educational purpose, with your professional judgement intact."
        />
        <div className="grid sm:grid-cols-3 gap-5" data-testid="ai-value-props">
          {[
            { icon: Sparkles, title: "Position before tools", text: "Decide where AI belongs in your practice before you change a single routine." },
            { icon: Pencil, title: "Full editable decks", text: "Every workshop is a complete PowerPoint you can self-study or present to staff." },
            { icon: Download, title: "Instant access", text: "One $49 payment unlocks all five workshops in the level, downloadable straight away." },
          ].map(({ icon: Icon, title, text }) => (
            <Card key={title} className="p-6 border-slate-200">
              <Icon className="w-6 h-6 text-teal-600" />
              <h3 className="font-display font-bold text-slate-900 mt-3">{title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section id="levels">
        <SectionHeading eyebrow="The series" title="Three levels, five workshops each"
          subtitle="Each level is $49 and includes all five workshop decks. Start with Foundation — Intermediate and Advanced are being released next." />
        {levels.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="ai-levels-empty">Loading the series…</p>
        ) : (
          <div className="space-y-8" data-testid="ai-level-list">
            {levels.map((lv, i) => <Level key={lv.id} level={lv} index={i} />)}
          </div>
        )}
      </Section>
    </div>
  );
}
